import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase, safeSupabaseQuery } from '../services/supabaseClient';
import { safeFetch } from '../utils/supabaseHelpers';
import { getProfileByIdFromBackend } from '../services/backendApi';

// Tipos de roles
export type UserRole = 'Administrador' | 'Contadora' | 'Usuario';

// Interfaz del usuario
export interface User {
  id: string;
  nombre: string;
  correo: string;
  rol: UserRole;
  ultimo_login: string;
  empresa: string;
  estado_erp: string;
  avatar?: string;
}

// Interfaz del contexto
interface AuthContextType {
  user: User | null;
  loading: boolean;
  session: any | null | undefined; // undefined = aún cargando, null = sin sesión, object = sesión activa
  login: (userData: User) => void;
  loginWithEmail: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, nombre: string) => Promise<{ success: boolean; error?: string; warning?: string }>;
  approveUser: (userId: string) => Promise<{ success: boolean; error?: string }>;
  rejectUser: (userId: string, motivo?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  switchRole: (role: UserRole) => void;
  hasPermission: (permission: string) => boolean;
  isAdmin: boolean;
  isContadora: boolean;
  isUsuario: boolean;
}

// Permisos por rol
const ROLE_PERMISSIONS = {
  Administrador: [
    'view_dashboard',
    'view_caja_chica',
    'view_reportes_bi',
    'edit_caja_chica',
    'edit_reportes_bi',
    'view_auditoria',
    'view_logs',
    'manage_users',
    'export_data',
    'sync_erp'
  ],
  Contadora: [
    'view_dashboard',
    'view_caja_chica',
    'edit_caja_chica',
    'approve_expenses',
    'view_auditoria',
    'view_logs_readonly',
    'export_data',
    'sync_erp'
  ],
  Usuario: [
    'view_dashboard',
    'view_caja_chica',
    'add_expenses',
    'view_own_expenses',
    'view_monthly_summary'
  ]
};

// Usuarios mock para simulación
const MOCK_USERS: User[] = [
  {
    id: '1',
    nombre: 'Antonia Meza',
    correo: 'antonia@selger.cl',
    rol: 'Administrador',
    ultimo_login: '22/10/2025 15:42:00',
    empresa: 'J&J Operations',
    estado_erp: 'Conectado con ERP Defontana',
    avatar: '👩‍💼'
  },
  {
    id: '2',
    nombre: 'J&J Operations',
    correo: 'selger@selger.cl',
    rol: 'Usuario',
    ultimo_login: '22/10/2025 14:30:00',
    empresa: 'J&J Operations',
    estado_erp: 'Conectado con ERP Defontana',
    avatar: '🏢'
  },
  {
    id: '3',
    nombre: 'Contabilidad',
    correo: 'contabilidad@selger.cl',
    rol: 'Contadora',
    ultimo_login: '22/10/2025 13:15:00',
    empresa: 'J&J Operations',
    estado_erp: 'Conectado con ERP Defontana',
    avatar: '🧮'
  },
  {
    id: '4',
    nombre: 'Metalmecánica',
    correo: 'metalmecanica@selger.cl',
    rol: 'Usuario',
    ultimo_login: '22/10/2025 12:45:00',
    empresa: 'J&J Operations',
    estado_erp: 'Conectado con ERP Defontana',
    avatar: '🛠️'
  }
];

// Crear contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider del contexto
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<any | null | undefined>(undefined); // undefined = aún cargando
  
  // ✅ OPTIMIZADO: Ref para comparar access_token y evitar actualizaciones innecesarias
  const prevSessionTokenRef = useRef<string | null>(null);
  // ✅ NUEVO: Ref para verificar si ya hay una sesión activa sin causar re-renders
  const hasActiveSessionRef = useRef<boolean>(false);

  // ✅ NUEVO: Limpiar sesión automáticamente al cerrar la pestaña/navegador
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('🔄 [AuthContext] Limpiando sesión al cerrar pestaña...');
      
      // Limpiar tokens de Supabase de sessionStorage (ya se limpian automáticamente al cerrar)
      // Pero limpiar también de localStorage por si acaso hay tokens antiguos
      const localKeys = Object.keys(localStorage);
      localKeys.forEach(key => {
        if (key.startsWith('sb-') || key.includes('supabase.auth.token')) {
          localStorage.removeItem(key);
        }
      });
      
      // Limpiar currentUser de localStorage
      localStorage.removeItem('currentUser');
      
      // Intentar cerrar sesión en Supabase (no bloqueante)
      supabase.auth.signOut().catch(() => {
        // Ignorar errores al cerrar sesión (la pestaña se está cerrando)
      });
    };

    // Agregar listener para cuando se cierra la pestaña
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Mapear rol desde Supabase a UserRole
  const mapRoleFromSupabase = (role: string | null): UserRole => {
    if (role === 'administrador' || role === 'Administrador') return 'Administrador';
    if (role === 'contadora' || role === 'Contadora') return 'Contadora';
    return 'Usuario';
  };

  // Mapear usuario de Supabase a User
  const mapSupabaseUserToUser = async (supabaseUser: any): Promise<User | null> => {
    try {
      // Intentar obtener información adicional del usuario desde la tabla profiles
      // Aumentar timeout para conexiones desde Netlify
      let userData = null;
      try {
        const profileQuery = supabase
          .from('profiles')
          .select('*')
          .eq('id', supabaseUser.id)
          .single();

        // Timeout aumentado a 5 segundos para conexiones desde Netlify
        // Si tarda más, simplemente usar user_metadata
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Profile query timeout')), 5000)
        );

        const result = await Promise.race([profileQuery, timeoutPromise]) as any;
        if (result?.data) {
          userData = result.data;
        }
      } catch (profileError: any) {
        // Si falla la consulta a profiles (timeout, error de RLS, etc.), continuar con user_metadata
        // Esto es normal si la tabla no existe o hay problemas de permisos
        // No loguear el error de timeout para no saturar la consola
        if (profileError?.message !== 'Profile query timeout') {
          console.warn('⚠️ No se pudo consultar profiles, usando user_metadata:', profileError?.message || 'Error desconocido');
        }
      }

      // Prioridad: user_metadata > profiles > email
      // La tabla profiles usa 'full_name' en lugar de 'nombre'
      const nombre = supabaseUser.user_metadata?.nombre || 
                     (userData && typeof userData === 'object' && 'full_name' in userData ? (userData as { full_name?: string }).full_name : undefined) || 
                     supabaseUser.email?.split('@')[0] || 
                     'Usuario';
      
      // ✅ SOLUCIÓN: Usar solo user_metadata para evitar bloqueos del RPC
      // El rol ya está disponible en user_metadata, no necesitamos RPC que puede bloquear el login
      let rolRaw = supabaseUser.user_metadata?.rol || supabaseUser.user_metadata?.role;
      console.log('🔍 [AuthContext] Rol desde user_metadata:', rolRaw);
      console.log('🔍 [AuthContext] Email del usuario:', supabaseUser.email);
      
      // ✅ ELIMINADO: Bloque del RPC que causaba bloqueos en el login
      // Si necesitas datos actualizados de la BD, implementa el backend como puente
      
      const rol = mapRoleFromSupabase(rolRaw || 'Usuario');
      console.log('🔍 [AuthContext] Rol mapeado final:', rol);
      
      // ✅ ELIMINADO: Verificación especial con RPC para usuarios específicos
      // El RPC no funciona, usar solo user_metadata que ya está disponible
      
      return {
        id: supabaseUser.id,
        nombre: nombre,
        correo: supabaseUser.email,
        rol: rol,
        ultimo_login: new Date().toISOString(), // Guardar como ISO string para poder parsearlo correctamente
        empresa: supabaseUser.user_metadata?.empresa || 'J&J Operations',
        estado_erp: 'Conectado con ERP Defontana',
        avatar: supabaseUser.user_metadata?.avatar || '👤'
      };
    } catch (error) {
      console.error('Error al mapear usuario:', error);
      // Fallback: crear usuario básico desde auth (sin rol de admin)
      return {
        id: supabaseUser.id,
        nombre: supabaseUser.email?.split('@')[0] || 'Usuario',
        correo: supabaseUser.email,
        rol: 'Usuario',
        ultimo_login: new Date().toISOString(), // Guardar como ISO string para poder parsearlo correctamente
        empresa: 'J&J Operations',
        estado_erp: 'Conectado con ERP Defontana',
        avatar: '👤'
      };
    }
  };

  // Función para limpiar tokens corruptos de Supabase
  const clearSupabaseTokens = () => {
    try {
      let cleaned = 0;
      
      // Limpiar de sessionStorage (ahora usamos sessionStorage)
      const sessionKeys = Object.keys(sessionStorage);
      sessionKeys.forEach(key => {
        if (key.startsWith('sb-') || key.includes('supabase.auth.token')) {
          sessionStorage.removeItem(key);
          cleaned++;
        }
      });
      
      // También limpiar localStorage por si acaso hay tokens antiguos
      const localKeys = Object.keys(localStorage);
      localKeys.forEach(key => {
        if (key.startsWith('sb-') || key.includes('supabase.auth.token')) {
          localStorage.removeItem(key);
          cleaned++;
        }
      });
      
      // ✅ CRÍTICO: Limpiar también currentUser si hay tokens corruptos
      // Esto evita que se intente usar datos de usuario con tokens inválidos
      if (localStorage.getItem('currentUser')) {
        localStorage.removeItem('currentUser');
        cleaned++;
        console.log('🧹 [LoadSession] currentUser limpiado debido a tokens corruptos');
      }
      
      if (cleaned > 0) {
        console.log(`🧹 [LoadSession] ${cleaned} tokens y datos de Supabase limpiados`);
      }
      return cleaned;
    } catch (error) {
      console.warn('⚠️ [LoadSession] Error al limpiar tokens:', error);
      return 0;
    }
  };

  // Función para verificar si hay tokens en storage (sin validar si están corruptos)
  const hasTokensInStorage = (): boolean => {
    try {
      // Verificar sessionStorage (ahora usamos sessionStorage)
      const sessionKeys = Object.keys(sessionStorage);
      const hasSessionTokens = sessionKeys.some(key => 
        key.startsWith('sb-') || key.includes('supabase.auth.token')
      );
      
      // También verificar localStorage por si acaso hay tokens antiguos
      const localKeys = Object.keys(localStorage);
      const hasLocalTokens = localKeys.some(key => 
        key.startsWith('sb-') || key.includes('supabase.auth.token')
      );
      
      return hasSessionTokens || hasLocalTokens;
    } catch {
      return false;
    }
  };

  // Función para detectar si hay tokens corruptos en sessionStorage
  const hasCorruptedTokens = (): boolean => {
    try {
      // Verificar sessionStorage (ahora usamos sessionStorage)
      const sessionKeys = Object.keys(sessionStorage);
      const supabaseSessionKeys = sessionKeys.filter(key => 
        key.startsWith('sb-') || key.includes('supabase.auth.token')
      );
      
      // También verificar localStorage por si acaso hay tokens antiguos
      const localKeys = Object.keys(localStorage);
      const supabaseLocalKeys = localKeys.filter(key => 
        key.startsWith('sb-') || key.includes('supabase.auth.token')
      );
      
      const supabaseKeys = [...supabaseSessionKeys, ...supabaseLocalKeys];
      
      // Si no hay tokens, no hay corrupción
      if (supabaseKeys.length === 0) return false;
      
      // Intentar leer y validar cada token
      for (const key of supabaseKeys) {
        try {
          const value = sessionStorage.getItem(key) || localStorage.getItem(key);
          if (value) {
            // Intentar parsear como JSON - si falla, el token está corrupto
            JSON.parse(value);
          }
        } catch (parseError) {
          // Si no se puede parsear, el token está corrupto
          console.warn(`⚠️ [LoadSession] Token corrupto detectado en clave: ${key}`);
          return true;
        }
      }
      
      // Todos los tokens son válidos
      return false;
    } catch (error) {
      // Si hay error al verificar, asumir que puede haber corrupción
      console.warn('⚠️ [LoadSession] Error al verificar tokens, asumiendo posible corrupción:', error);
      return true;
    }
  };

  // ✅ CORREGIDO: Cargar sesión sin bloquear UI, crear usuario temporal
  useEffect(() => {
    let isMounted = true;
    let subscription: any = null;
    let loginCheckInterval: NodeJS.Timeout | null = null; // ✅ NUEVO: Referencia al intervalo de verificación de login
    
    // ✅ NUEVO: Función para limpiar el intervalo de verificación de login
    const clearLoginCheckInterval = () => {
      if (loginCheckInterval) {
        clearInterval(loginCheckInterval);
        loginCheckInterval = null;
        console.log('🧹 [AuthContext] Intervalo de verificación de login limpiado');
      }
    };

    // ✅ Función auxiliar para cargar la sesión (evita duplicación de código)
    const executeSessionLoad = async () => {
      if (!isMounted) return;

      // ✅ DEBUG: Log cuando se ejecuta executeSessionLoad
      console.log('🔍 [ExecuteSessionLoad] ========================================');
      console.log('🔍 [ExecuteSessionLoad] executeSessionLoad llamado');
      console.log('🔍 [ExecuteSessionLoad] Timestamp:', new Date().toISOString());
      console.trace('🔍 [ExecuteSessionLoad] Llamada desde:');
      console.log('🔍 [ExecuteSessionLoad] ========================================');

      // ✅ CRÍTICO: NO ejecutar si ya hay una sesión activa - evitar timeouts innecesarios
      if (hasActiveSessionRef.current) {
        console.log('ℹ️ [ExecuteSessionLoad] Sesión activa detectada (ref), NO ejecutando - evitando timeout innecesario');
        return; // ✅ Salir temprano - ya hay una sesión activa
      }

      // ✅ CRÍTICO: NO ejecutar si hay login en progreso - dejar que onAuthStateChange lo maneje
      const loginInProgress = sessionStorage.getItem('login_in_progress');
      if (loginInProgress) {
        console.log('ℹ️ [ExecuteSessionLoad] Login en progreso detectado, NO ejecutando - onAuthStateChange manejará el login');
        return; // ✅ Salir temprano - onAuthStateChange ya maneja el login
      }

      try {
        // ✅ Obtener session y user en paralelo CON TIMEOUT
        console.log('🔄 [LoadSession] Obteniendo sesión y usuario en paralelo...');
        
        // ✅ TIMEOUT aumentado a 10 segundos para dar más tiempo a Supabase
        // Si getSession/getUser no responden en 10 segundos, asumir que no hay sesión
        const TIMEOUT_MS = 10000; // 10 segundos
        
        const getSessionWithTimeout = Promise.race([
          supabase.auth.getSession(),
          new Promise<{ data: { session: null }, error: { message: string } }>((resolve) => {
            setTimeout(() => {
              console.warn('⏱️ [LoadSession] Timeout en getSession (10s), asumiendo que no hay sesión');
              resolve({ data: { session: null }, error: { message: 'Timeout' } });
            }, TIMEOUT_MS);
          })
        ]).catch((error) => {
          console.error('❌ [LoadSession] Error en getSession:', error);
          return { data: { session: null }, error };
        });

        const getUserWithTimeout = Promise.race([
          supabase.auth.getUser(),
          new Promise<{ data: { user: null }, error: { message: string } }>((resolve) => {
            setTimeout(() => {
              console.warn('⏱️ [LoadSession] Timeout en getUser (10s)');
              resolve({ data: { user: null }, error: { message: 'Timeout' } });
            }, TIMEOUT_MS);
          })
        ]).catch((error) => {
          console.error('❌ [LoadSession] Error en getUser:', error);
          return { data: { user: null }, error };
        });

        const [sessionResult, userResult] = await Promise.all([
          getSessionWithTimeout,
          getUserWithTimeout
        ]);

        if (!isMounted) return;

        const { data: { session }, error: sessionError } = sessionResult;
        const { data: { user }, error: userError } = userResult;

        // ✅ Si hay timeout, establecer session=null y limpiar tokens
        if (sessionError?.message === 'Timeout' || userError?.message === 'Timeout') {
          console.warn('⏱️ [LoadSession] Timeout detectado, limpiando tokens y estableciendo session=null');
          
          // ✅ NUEVO: Contar timeouts para detectar tokens persistentemente corruptos
          const timeoutCount = parseInt(sessionStorage.getItem('session_timeout_count') || '0') + 1;
          sessionStorage.setItem('session_timeout_count', timeoutCount.toString());
          
          // Si hay múltiples timeouts (2 o más), los tokens probablemente están corruptos
          if (timeoutCount >= 2) {
            console.warn('⚠️ [LoadSession] Múltiples timeouts detectados, limpiando tokens corruptos...');
            clearSupabaseTokens();
            sessionStorage.removeItem('session_timeout_count');
          } else {
            // Primer timeout, limpiar tokens pero mantener el contador
            clearSupabaseTokens();
          }
          
          // ✅ CRÍTICO: Asegurar que loading se establezca en false para evitar que se quede en "Verificando sesión"
          // ✅ CRÍTICO: Limpiar cualquier flag de login en progreso para evitar loops
          // ✅ CRÍTICO: Limpiar también cualquier intervalo de verificación de login que pueda estar activo
          if (loginCheckInterval) {
            clearInterval(loginCheckInterval);
            loginCheckInterval = null;
            console.log('🧹 [LoadSession] Intervalo de verificación de login limpiado después de timeout');
          }
          sessionStorage.removeItem('login_in_progress');
          sessionStorage.removeItem('login_start_time');
          setLoading(false);
          prevSessionTokenRef.current = null;
          hasActiveSessionRef.current = false; // ✅ NUEVO: Marcar que NO hay sesión activa
          setSession(null);
          setUser(null);
          localStorage.removeItem('currentUser');
          console.log('✅ [LoadSession] Timeout manejado, loading=false, session=null, flags limpiados');
          return;
        }
        
        // ✅ NUEVO: Si no hay timeout, resetear el contador
        sessionStorage.removeItem('session_timeout_count');

        if (sessionError) {
          console.error('❌ [LoadSession] Error al obtener sesión:', sessionError.message);

          if (sessionError.message?.includes('Invalid') ||
              sessionError.message?.includes('expired') ||
              sessionError.message?.includes('token') ||
              sessionError.status === 401 ||
              sessionError.status === 400) {
            console.warn('🧹 [LoadSession] Tokens corruptos detectados en getSession, limpiando...');
            clearSupabaseTokens();
          }

          // ✅ CRÍTICO: Asegurar que loading se establezca en false para evitar que se quede en "Verificando sesión"
          setLoading(false);
          setSession(null);
          setUser(null);
          return;
        }

        // ✅ NO hacer refresh manual - Supabase maneja el refresh automáticamente
        // ✅ SOLO establecer session cuando Supabase confirme que existe
        if (session) {
          console.log('✅ [LoadSession] Sesión encontrada, usando directamente (refresh automático activo)');
          prevSessionTokenRef.current = session.access_token;
          hasActiveSessionRef.current = true; // ✅ NUEVO: Marcar que hay sesión activa
          setSession(session); // ✅ Establecer session solo cuando confirmemos que existe
        } else {
          prevSessionTokenRef.current = null;
          hasActiveSessionRef.current = false; // ✅ NUEVO: Marcar que NO hay sesión activa
          setSession(null); // ✅ SOLO establecer null cuando confirmemos que NO hay sesión
        }

        if (!session) {
          console.log('ℹ️ [LoadSession] No hay sesión activa');
          // ✅ CRÍTICO: Asegurar que loading se establezca en false para evitar que se quede en "Verificando sesión"
          setLoading(false);
          setUser(null);
          // Limpiar currentUser si no hay sesión
          localStorage.removeItem('currentUser');
          return;
        }

        if (userError || !user) {
          console.error('❌ [LoadSession] Error al obtener usuario:', userError?.message);
          // ✅ CRÍTICO: Asegurar que loading se establezca en false para evitar que se quede en "Verificando sesión"
          setLoading(false);
          setUser(null);
          // Limpiar currentUser si no hay usuario
          localStorage.removeItem('currentUser');
          return;
        }

        // ✅ CREAR USUARIO TEMPORAL INMEDIATAMENTE con datos de metadata
        const tempUser: User = {
          id: user.id,
          nombre: user.user_metadata?.nombre || user.email?.split('@')[0] || 'Usuario',
          correo: user.email || '',
          rol: mapRoleFromSupabase(user.user_metadata?.rol || user.user_metadata?.role || 'Usuario'),
          ultimo_login: new Date().toISOString(),
          empresa: user.user_metadata?.empresa || 'J&J Operations',
          estado_erp: 'Conectado con ERP Defontana',
          avatar: user.user_metadata?.avatar || '👤'
        };

        setUser(tempUser);
        localStorage.setItem('currentUser', JSON.stringify(tempUser));
        console.log('✅ [LoadSession] Usuario temporal creado');

        // ✅ Cargar perfil después (no bloqueante)
        setTimeout(async () => {
          if (!isMounted) return;

          try {
            // 3. Verificar acceso antes de mapear usuario (usar backend como puente)
            let profileData: any = null;

            // ✅ Intentar primero con backend como puente
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            if (BACKEND_URL) {
              try {
                console.log('🔍 [LoadSession] Intentando obtener perfil desde backend (puente con Supabase)...');
                const backendResult = await getProfileByIdFromBackend(user.id);

                if (backendResult.success && backendResult.data) {
                  console.log(`✅ [LoadSession] Perfil obtenido desde backend${backendResult.cached ? ' (cache)' : ''}`);
                  profileData = backendResult.data;
                }
              } catch (backendError: any) {
                console.warn('⚠️ [LoadSession] Backend falló, intentando Supabase directo:', backendError?.message);
                // Continuar con Supabase directo como fallback
              }
            } else {
              console.log('ℹ️ [LoadSession] Backend URL no configurada, usando Supabase directo');
            }

            // ✅ FALLBACK: Si backend no está disponible o falla, usar Supabase directo
            if (!profileData) {
              try {
                console.log('🔍 [LoadSession] Consultando perfil directamente desde Supabase...');
                
                // ✅ AGREGAR TIMEOUT para evitar que se quede colgado por RLS
                const TIMEOUT_MS = 5000; // 5 segundos
                
                const profileQuery = supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', user.id)
                  .single();
                
                const profileTimeout = new Promise<{ data: null, error: { message: string; code?: string } }>((resolve) => {
                  setTimeout(() => {
                    console.warn('⏱️ [LoadSession] Timeout al consultar profiles (5s) - posible problema de RLS');
                    resolve({ data: null, error: { message: 'Timeout al consultar profiles', code: 'TIMEOUT' } });
                  }, TIMEOUT_MS);
                });
                
                const result = await Promise.race([profileQuery, profileTimeout]);
                const { data, error } = result;

                if (error) {
                  console.error('❌ [LoadSession] Error al obtener perfil:', error.message);
                  
                  // ✅ Si es error de RLS, dar mensaje más específico
                  if (error.message?.includes('permission') || 
                      error.message?.includes('policy') || 
                      error.message?.includes('RLS') ||
                      error.code === 'PGRST301' ||
                      error.code === 'TIMEOUT' ||
                      error.message === 'Timeout al consultar profiles') {
                    console.error('🚫 [LoadSession] Error de permisos RLS o timeout detectado');
                    console.error('💡 [LoadSession] Verifica que exista una política RLS que permita:');
                    console.error('   SELECT en profiles WHERE id = auth.uid()');
                  }
                  
                  // ✅ NO hacer logout si falla el perfil, mantener usuario temporal
                  console.warn('⚠️ [LoadSession] Manteniendo usuario temporal, perfil no disponible');
                  return;
                }

                profileData = data;
              } catch (profileError: any) {
                console.error('❌ [LoadSession] Error al consultar profiles:', profileError?.message);
                // ✅ NO hacer logout si falla el perfil, mantener usuario temporal
                console.warn('⚠️ [LoadSession] Manteniendo usuario temporal, perfil no disponible');
                return;
              }
            }

            if (!isMounted) return;

            // 4. Verificar que acceso sea true
            if (!profileData || (profileData.acceso !== true && profileData.acceso !== 'true' && profileData.acceso !== 1)) {
              console.warn('⚠️ [LoadSession] Usuario sin acceso aprobado');
              await supabase.auth.signOut();
              setUser(null);
              setSession(null);
              return;
            }

            // 5. Mapear usuario completo solo si acceso está aprobado
            const mappedUser = await mapSupabaseUserToUser(user);

            if (!isMounted) return;

            if (mappedUser) {
              // ✅ Actualizar user con datos completos del perfil
              setUser(mappedUser);
              localStorage.setItem('currentUser', JSON.stringify(mappedUser));
              console.log('✅ [LoadSession] Perfil completo cargado y usuario actualizado');
            } else {
              // ✅ NO hacer logout, mantener usuario temporal
              console.warn('⚠️ [LoadSession] No se pudo mapear usuario completo, manteniendo usuario temporal');
            }
          } catch (error: any) {
            console.error('❌ [LoadSession] Error al cargar perfil:', error);
            // ✅ NO hacer logout si falla la carga del perfil, mantener usuario temporal
            // El usuario ya puede ver la interfaz con datos temporales
            console.warn('⚠️ [LoadSession] Manteniendo usuario temporal debido a error en carga de perfil');
          }
        }, 0); // Ejecutar en el siguiente tick

      } catch (error: any) {
        console.error('❌ [LoadSession] Error al obtener sesión/usuario:', error);
        // ✅ Limpiar todo en caso de error
        // ✅ CRÍTICO: Asegurar que loading se establezca en false para evitar que se quede en "Verificando sesión"
        setLoading(false);
        clearSupabaseTokens();
        setSession(null);
        setUser(null);
      }
    };

    const loadSession = async () => {
      console.log('🔄 [LoadSession] Iniciando carga de sesión...');
      console.log('🔍 [LoadSession] ========================================');
      console.log('🔍 [LoadSession] loadSession llamado');
      console.log('🔍 [LoadSession] Timestamp:', new Date().toISOString());
      console.trace('🔍 [LoadSession] Llamada desde:');
      console.log('🔍 [LoadSession] ========================================');

      // ✅ CRÍTICO: NO establecer session=null al inicio
      // Mantener session=undefined hasta que Supabase responda
      // Solo establecer loading=false para permitir que la UI se renderice
      setLoading(false);
      console.log('✅ [LoadSession] Loading establecido en false, session permanece undefined');
      // NO establecer setSession(null) aquí - mantener undefined

      try {
        // ✅ Verificar tokens corruptos ANTES de intentar getSession
        if (hasCorruptedTokens()) {
          console.warn('🧹 [LoadSession] Tokens corruptos detectados, limpiando preventivamente...');
          clearSupabaseTokens();
          // ✅ SOLO establecer null DESPUÉS de limpiar tokens corruptos
          setSession(null);
          setUser(null);
          return; // Salir temprano - no intentar getSession con tokens corruptos
        } else {
          console.log('✅ [LoadSession] Tokens válidos detectados, continuando...');
        }

        // ✅ CRÍTICO: Verificar si hay un login en progreso ANTES de intentar cargar sesión
        // Esto aplica tanto si hay tokens como si no hay tokens
        // ✅ NUEVO: Si hay múltiples timeouts, NO esperar login en progreso - limpiar inmediatamente
        const timeoutCount = parseInt(sessionStorage.getItem('session_timeout_count') || '0');
        const loginInProgress = sessionStorage.getItem('login_in_progress');
        
        // ✅ CRÍTICO: Si hay múltiples timeouts, limpiar login en progreso inmediatamente
        if (loginInProgress && timeoutCount >= 2) {
          console.warn('⚠️ [LoadSession] Múltiples timeouts detectados, limpiando login en progreso stuck...');
          sessionStorage.removeItem('login_in_progress');
          sessionStorage.removeItem('login_start_time');
          // Continuar con carga normal de sesión (no retornar)
        } else if (loginInProgress) {
          // ✅ NUEVO: Verificar si el login está "stuck" (más de 30 segundos sin timestamp)
          // Si no hay timestamp o es muy antiguo, limpiar el flag
          const loginStartTime = sessionStorage.getItem('login_start_time');
          if (loginStartTime) {
            const elapsed = Date.now() - parseInt(loginStartTime);
            if (elapsed > 30000) { // 30 segundos
              console.warn('⚠️ [LoadSession] Login en progreso por más de 30s, limpiando flag stuck...');
              sessionStorage.removeItem('login_in_progress');
              sessionStorage.removeItem('login_start_time');
              // Continuar con carga normal de sesión (no retornar)
            } else {
              // Login válido en progreso, esperar
              console.log('ℹ️ [LoadSession] Login en progreso detectado, esperando a que complete...');
              // ✅ Esperar hasta 15 segundos para que el login complete
              let attempts = 0;
              const maxAttempts = 15; // 15 intentos = 15 segundos máximo
              
              const checkLoginComplete = setInterval(() => {
                if (!isMounted) {
                  clearInterval(checkLoginComplete);
                  loginCheckInterval = null;
                  return;
                }
                
                attempts++;
                
                // Si el login ya no está en progreso, intentar cargar sesión
                if (!sessionStorage.getItem('login_in_progress')) {
                  clearInterval(checkLoginComplete);
                  loginCheckInterval = null;
                  console.log('✅ [LoadSession] Login completado, cargando sesión...');
                  setTimeout(() => executeSessionLoad(), 500); // Esperar 500ms más para asegurar que los tokens estén guardados
                  return;
                }
                
                // Si pasaron 15 segundos, asumir que el login falló o hay problema
                if (attempts >= maxAttempts) {
                  clearInterval(checkLoginComplete);
                  loginCheckInterval = null;
                  console.warn('⏱️ [LoadSession] Timeout esperando login (15s), limpiando flag y cargando sesión...');
                  // ✅ CRÍTICO: Limpiar el flag si está stuck
                  sessionStorage.removeItem('login_in_progress');
                  sessionStorage.removeItem('login_start_time');
                  // Intentar cargar sesión de todas formas
                  setTimeout(() => executeSessionLoad(), 0);
                  return;
                }
              }, 1000); // Verificar cada segundo
              
              loginCheckInterval = checkLoginComplete;
              
              return; // Salir temprano, el setInterval manejará el resto
            }
          } else {
            // No hay timestamp, puede ser un flag stuck de sesión anterior, limpiarlo
            console.warn('⚠️ [LoadSession] Login en progreso sin timestamp, limpiando flag stuck...');
            sessionStorage.removeItem('login_in_progress');
            // Continuar con carga normal de sesión (no retornar)
          }
        }

        // ✅ Verificar si hay tokens antes de intentar getSession()
        // Si no hay tokens, esperar un momento antes de establecer session=null
        // Esto da tiempo a que onAuthStateChange maneje SIGNED_IN si hay un login en progreso
        if (!hasTokensInStorage()) {
          console.log('ℹ️ [LoadSession] No hay tokens en storage, esperando un momento...');
          
          // ✅ CRÍTICO: Verificar si hay un logout en progreso
          // Si hay logout en progreso, verificar si está "stuck" (más de 2 segundos)
          const logoutInProgress = sessionStorage.getItem('logout_in_progress');
          if (logoutInProgress) {
            const logoutTime = sessionStorage.getItem('logout_time');
            const elapsed = logoutTime ? Date.now() - parseInt(logoutTime) : Infinity;
            
            // ✅ CORREGIDO: Si el logout está "stuck" (más de 2 segundos), limpiarlo
            if (elapsed > 2000) {
              console.warn('⚠️ [LoadSession] Logout en progreso está stuck (más de 2s), limpiando flag...');
              sessionStorage.removeItem('logout_in_progress');
              sessionStorage.removeItem('logout_time');
              // Continuar con carga normal de sesión (no retornar, seguir al código de abajo)
            } else {
            console.log('ℹ️ [LoadSession] Logout en progreso detectado, esperando a que complete...');
            // Esperar más tiempo (1.5 segundos) para que el logout complete completamente
            setTimeout(() => {
              if (!isMounted) return;
              
                // ✅ CORREGIDO: Intentar cargar sesión de Supabase de todas formas
                // Si hay sesión válida, la usaremos; si no, estableceremos null
                console.log('🔄 [LoadSession] Intentando cargar sesión después del delay de logout...');
                executeSessionLoad();
            }, 1500); // Esperar 1.5 segundos si hay logout en progreso
            
            return; // Salir temprano, el setTimeout manejará el resto
            }
          }
          
          // ✅ CRÍTICO: Verificar si hay un login en progreso
          // Si hay login en progreso, NO ejecutar loadSession - dejar que onAuthStateChange lo maneje
          const loginInProgress = sessionStorage.getItem('login_in_progress');
          if (loginInProgress) {
            console.log('ℹ️ [LoadSession] Login en progreso detectado, NO ejecutando loadSession - onAuthStateChange manejará el login');
            // ✅ NO ejecutar loadSession - simplemente salir y dejar que onAuthStateChange maneje el login
            // onAuthStateChange se activará cuando el login complete y manejará SIGNED_IN
            return; // ✅ Salir temprano - onAuthStateChange manejará el login
          }
          
          // ✅ Esperar 500ms antes de establecer session=null
          // Esto da tiempo a que onAuthStateChange maneje SIGNED_IN si hay un login en progreso
          setTimeout(() => {
            if (!isMounted) return;
            
            // Verificar nuevamente si hay tokens (puede que se hayan guardado durante el delay)
            if (!hasTokensInStorage()) {
              console.log('ℹ️ [LoadSession] Aún no hay tokens después del delay, estableciendo session=null');
              setSession(null);
              setUser(null);
              localStorage.removeItem('currentUser');
            } else {
              console.log('✅ [LoadSession] Tokens encontrados después del delay, continuando con carga de sesión');
              // Continuar con el flujo normal de carga de sesión
              // Si onAuthStateChange ya estableció el usuario, executeSessionLoad() encontrará la misma sesión
              // y actualizará el estado de manera consistente
              setTimeout(() => executeSessionLoad(), 0);
            }
          }, 500); // Esperar 500ms antes de establecer session=null
          
          return; // Salir temprano, el setTimeout manejará el resto
        }

        // ✅ Cargar sesión en segundo plano (no bloquea UI)
        // Usar setTimeout para diferir la carga
        setTimeout(() => executeSessionLoad(), 0);

      } catch (error: any) {
        console.error('❌ [LoadSession] Error inesperado:', error);
        if (isMounted) {
          // ✅ Limpiar todo en caso de error
          clearSupabaseTokens();
          setUser(null);
          setSession(null);
        }
      }
    };

    loadSession();

    // ✅ CORREGIDO: Escuchar cambios en la autenticación con sincronización REAL de sesión
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      // ✅ DEBUG: Log detallado de eventos de autenticación
      console.log('🔍 [onAuthStateChange] ========================================');
      console.log('🔍 [onAuthStateChange] Evento recibido:', event);
      console.log('🔍 [onAuthStateChange] Session:', session ? 'existe' : 'null');
      console.log('🔍 [onAuthStateChange] Timestamp:', new Date().toISOString());
      console.trace('🔍 [onAuthStateChange] Stack trace:');
      console.log('🔍 [onAuthStateChange] ========================================');

      // ✅ CRÍTICO: Si hay un timeout reciente, NO procesar eventos de auth hasta que se limpie
      const timeoutCount = parseInt(sessionStorage.getItem('session_timeout_count') || '0');
      if (timeoutCount >= 2) {
        console.log('⚠️ [onAuthStateChange] Ignorando evento debido a múltiples timeouts:', event);
        console.trace('⚠️ [onAuthStateChange] Evento bloqueado desde:');
        return;
      }

      // ✅ OPTIMIZADO: Solo actualizar estado si la sesión realmente cambió (comparar access_token)
      if (session) {
        const currentToken = session.access_token;
        if (prevSessionTokenRef.current !== currentToken) {
          prevSessionTokenRef.current = currentToken;
          hasActiveSessionRef.current = true; // ✅ NUEVO: Marcar que hay sesión activa
          setSession(session);
          // ✅ CRÍTICO: Establecer loading=false cuando se detecta una sesión activa
          // Esto permite que la UI se muestre inmediatamente, incluso antes de mapear el usuario completo
          setLoading(false);
          console.log('✅ [onAuthStateChange] Sesión establecida, loading=false');
        }
      } else {
        // Si no hay sesión, limpiar el ref y actualizar estado
        if (prevSessionTokenRef.current !== null) {
          prevSessionTokenRef.current = null;
          hasActiveSessionRef.current = false; // ✅ NUEVO: Marcar que NO hay sesión activa
          setSession(null);
          // ✅ CRÍTICO: Asegurar que loading esté en false cuando no hay sesión
          setLoading(false);
        }
      }

      if (event === 'SIGNED_IN' && session?.user) {
        // ✅ CRÍTICO: Limpiar flag de login en progreso inmediatamente cuando se detecta SIGNED_IN
        // Esto permite que el intervalo de verificación de login se detenga inmediatamente
        sessionStorage.removeItem('login_in_progress');
        sessionStorage.removeItem('login_start_time');
        console.log('✅ [onAuthStateChange] SIGNED_IN detectado, flags de login limpiados');
        console.log('🔍 [SIGNED_IN] Iniciando proceso de mapeo de usuario...');
        console.log('🔍 [SIGNED_IN] User ID:', session.user.id);
        console.log('🔍 [SIGNED_IN] User email:', session.user.email);
        
        try {
          // Consultar perfil según rol (misma lógica que login)
          const userMetadata = session.user.user_metadata || {};
          const userRole = userMetadata.rol || userMetadata.role;
          const isAdmin = userRole === 'Administrador';
          const isContabilidad = userRole === 'Contabilidad';
          
          console.log('🔍 [SIGNED_IN] Consultando perfil desde tabla profiles...');
          
          // ✅ MEJORADO: Intentar primero Supabase directo, luego backend API como fallback
          let profileData: any = null;
          let profileError: any = null;
          
          // 1. Intentar Supabase directo primero
          try {
            console.log('🔍 [SIGNED_IN] Intento 1: Consultando desde Supabase directo...');
            console.log('🔍 [SIGNED_IN] User ID para consulta:', session.user.id);
            const startTime = Date.now();
            
            const queryPromise = safeSupabaseQuery(
              async () => {
                console.log('🔍 [SIGNED_IN] Dentro de safeSupabaseQuery, ejecutando consulta...');
                const queryResult = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', session.user.id)
                  .single();
                console.log('🔍 [SIGNED_IN] Consulta completada, resultado:', {
                  hasData: !!queryResult.data,
                  hasError: !!queryResult.error,
                  errorCode: queryResult.error?.code,
                  errorMessage: queryResult.error?.message
                });
                return queryResult;
              },
              {
                timeout: 8000, // 8 segundos para consulta de perfil (reducido para ser más rápido)
                retries: 1 // Solo 1 reintento
              }
            );
            
            // Timeout manual adicional como seguridad
            const manualTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Timeout manual (10s)')), 10000)
            );
            
            let result: any;
            try {
              result = await Promise.race([queryPromise, manualTimeout]);
            } catch (timeoutError: any) {
              console.warn('⚠️ [SIGNED_IN] Timeout en Supabase directo, intentando backend API...');
              result = {
                data: null,
                error: {
                  code: 'TIMEOUT_ERROR',
                  message: 'Timeout en consulta a Supabase'
                }
              };
            }
            
            const elapsed = Date.now() - startTime;
            console.log(`⏱️ [SIGNED_IN] Consulta a Supabase completada en ${elapsed}ms`);
            
            if (result?.error) {
              if (result.error.code === 'PGRST116') {
                console.error('❌ [SIGNED_IN] Perfil no encontrado (PGRST116)');
                profileError = result.error;
              } else if (result.error.code === 'TIMEOUT_ERROR' || result.error.message?.includes('timeout')) {
                console.warn('⚠️ [SIGNED_IN] Timeout en Supabase, intentando backend API...');
                profileError = result.error;
              } else {
                console.warn('⚠️ [SIGNED_IN] Error en Supabase, intentando backend API...');
                profileError = result.error;
              }
            } else {
              console.log('✅ [SIGNED_IN] Perfil obtenido desde Supabase:', {
                id: result?.data?.id,
                email: result?.data?.email,
                acceso: result?.data?.acceso
              });
              profileData = result?.data;
            }
          } catch (supabaseError: any) {
            console.warn('⚠️ [SIGNED_IN] Excepción en Supabase, intentando backend API...');
            profileError = supabaseError;
          }
          
          // 2. Si Supabase falla, intentar backend API como fallback
          if (!profileData && profileError) {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL;
            if (backendUrl) {
              try {
                console.log('🔍 [SIGNED_IN] Intento 2: Consultando desde backend API...');
                const backendResult = await getProfileByIdFromBackend(session.user.id);
                
                if (backendResult.success && backendResult.data) {
                  console.log('✅ [SIGNED_IN] Perfil obtenido desde backend API:', {
                    id: backendResult.data.id,
                    email: backendResult.data.email,
                    acceso: backendResult.data.acceso
                  });
                  profileData = backendResult.data;
                  profileError = null; // Limpiar error ya que backend funcionó
                } else {
                  console.warn('⚠️ [SIGNED_IN] Backend API no devolvió datos válidos');
                }
              } catch (backendError: any) {
                console.warn('⚠️ [SIGNED_IN] Backend API también falló:', backendError?.message);
              }
            } else {
              console.warn('⚠️ [SIGNED_IN] Backend URL no configurada, no se puede usar como fallback');
            }
          }
          
          // ✅ MEJORADO: Si no hay profileData, usar user_metadata como fallback
          if (!profileData) {
            console.warn('⚠️ [SIGNED_IN] No se pudo obtener perfil del usuario desde Supabase ni backend');
            console.warn('⚠️ [SIGNED_IN] Usando user_metadata como fallback para permitir acceso');
            
            // ✅ NUEVO: Permitir acceso con user_metadata si no se puede obtener el perfil
            // Esto evita que los usuarios se queden bloqueados por problemas de RLS o conectividad
            const mappedUser = await mapSupabaseUserToUser(session.user);
            
            if (mappedUser) {
              console.log('✅ [SIGNED_IN] Usuario mapeado desde user_metadata (fallback):', {
                id: mappedUser.id,
                nombre: mappedUser.nombre,
                rol: mappedUser.rol
              });
              setUser(mappedUser);
              localStorage.setItem('currentUser', JSON.stringify(mappedUser));
              setLoading(false);
              console.log('✅ [SIGNED_IN] Usuario establecido desde fallback, sesión iniciada correctamente');
              return; // Salir aquí, no verificar acceso ya que no hay profileData
            } else {
              console.error('❌ [SIGNED_IN] No se pudo mapear el usuario ni desde perfil ni desde user_metadata');
              await supabase.auth.signOut();
              return;
            }
          }

          // Verificar acceso - SIEMPRE verificar que acceso sea true (solo si tenemos profileData)
          console.log('🔍 [SIGNED_IN] Verificando acceso del usuario...', {
            acceso: profileData.acceso,
            tipo: typeof profileData.acceso
          });
          
          if (profileData.acceso !== true && profileData.acceso !== 'true' && profileData.acceso !== 1) {
            console.warn('⚠️ [SIGNED_IN] Usuario sin acceso aprobado, cerrando sesión...');
            await supabase.auth.signOut();
            return;
          }
          
          console.log('✅ [SIGNED_IN] Acceso verificado, mapeando usuario...');
          
          // Mapear y establecer usuario solo si acceso está aprobado
          const mappedUser = await mapSupabaseUserToUser(session.user);
          
          if (mappedUser) {
            console.log('✅ [SIGNED_IN] Usuario mapeado exitosamente:', {
              id: mappedUser.id,
              nombre: mappedUser.nombre,
              rol: mappedUser.rol
            });
            setUser(mappedUser);
            localStorage.setItem('currentUser', JSON.stringify(mappedUser));
            // ✅ CRÍTICO: Establecer loading=false cuando el usuario está completamente cargado
            setLoading(false);
            console.log('✅ [SIGNED_IN] Usuario establecido y loading=false, sesión iniciada correctamente');
          } else {
            console.error('❌ [SIGNED_IN] No se pudo mapear el usuario, cerrando sesión...');
            await supabase.auth.signOut();
            return;
          }
        } catch (error: any) {
          // ✅ CORREGIDO: SIEMPRE cerrar sesión si no se puede verificar acceso
          console.error('❌ [SIGNED_IN] Error al verificar acceso:', error);
          console.error('❌ [SIGNED_IN] Stack trace:', error?.stack);
          await supabase.auth.signOut();
          return;
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('🔄 [AuthContext] Evento SIGNED_OUT detectado, limpiando sesión...');
        setUser(null);
        localStorage.removeItem('currentUser');
        setLoading(false);
        console.log('✅ [AuthContext] Sesión limpiada completamente');
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('🔄 [AuthContext] Token refrescado automáticamente');
        
        // ✅ OPTIMIZADO: Actualizar la sesión solo si el token cambió
        if (session) {
          const currentToken = session.access_token;
          if (prevSessionTokenRef.current !== currentToken) {
            prevSessionTokenRef.current = currentToken;
            setSession(session);
          }
          
          try {
            console.log('🔍 [TOKEN_REFRESHED] Remapeando usuario después del token refresh...');
            
            // Intentar mapear el usuario completo
            const mappedUser = await mapSupabaseUserToUser(session.user);
            
            if (mappedUser) {
              setUser(mappedUser);
              localStorage.setItem('currentUser', JSON.stringify(mappedUser));
              console.log('✅ [TOKEN_REFRESHED] Usuario remapeado exitosamente');
            } else {
              console.warn('⚠️ [TOKEN_REFRESHED] No se pudo mapear usuario después del refresh');
            }
          } catch (error: any) {
            console.error('❌ [TOKEN_REFRESHED] Error al remapear usuario:', error);
            
            // Si falla por timeout o error RPC, usar fallback con user_metadata
            if (error?.message?.includes('timeout') || error?.message?.includes('RPC')) {
              console.warn('⚠️ [TOKEN_REFRESHED] Timeout al remapear usuario. Usando fallback user_metadata');
              
              // ✅ CORREGIDO: Usar la interfaz User correcta
              const fallbackUser: User = {
                id: session.user.id,
                nombre: session.user.user_metadata?.nombre || session.user.email?.split('@')[0] || 'Usuario',
                correo: session.user.email || '',
                rol: mapRoleFromSupabase(session.user.user_metadata?.rol || session.user.user_metadata?.role || 'Usuario'),
                ultimo_login: new Date().toISOString(),
                empresa: session.user.user_metadata?.empresa || 'J&J Operations',
                estado_erp: 'Conectado con ERP Defontana',
                avatar: session.user.user_metadata?.avatar || '👤'
              };
              
              setUser(fallbackUser);
              localStorage.setItem('currentUser', JSON.stringify(fallbackUser));
              
              console.log('✅ [TOKEN_REFRESHED] Usuario fallback aplicado correctamente');
            }
          }
        }
      }
    });

    subscription = authSubscription;

    return () => {
      isMounted = false;
      // ✅ CRÍTICO: Limpiar el intervalo de verificación de login al desmontar
      if (loginCheckInterval) {
        clearInterval(loginCheckInterval);
        loginCheckInterval = null;
      }
      if (subscription) {
        subscription.unsubscribe();
      }
      // ✅ CRÍTICO: Limpiar intervalo de verificación de login si existe
      // Esto previene memory leaks si el componente se desmonta durante el login
      if (loginCheckInterval) {
        clearInterval(loginCheckInterval);
        loginCheckInterval = null;
        console.log('🧹 [AuthContext] Intervalo de verificación de login limpiado');
      }
    };
  }, []);

  // Función de login optimizada: signInWithPassword → getSession → getUser → consultar perfil → setUser
  const loginWithEmail = async (email: string, password: string): Promise<boolean> => {
    const startTime = Date.now();
    
    // ✅ DEBUG: Capturar stack trace completo para identificar quién está llamando a loginWithEmail
    const stackTrace = new Error().stack;
    console.log('🔍 [Login] ========================================');
    console.log('🔍 [Login] loginWithEmail llamado');
    console.log('🔍 [Login] Email:', email);
    console.log('🔍 [Login] Timestamp:', new Date().toISOString());
    console.log('🔍 [Login] Stack trace completo:');
    console.trace('🔍 [Login] Llamada desde:');
    console.log('🔍 [Login] ========================================');
    
    // ✅ CRÍTICO: Verificar si hay múltiples timeouts antes de permitir login
    // Si hay múltiples timeouts, NO permitir login automático
    const timeoutCount = parseInt(sessionStorage.getItem('session_timeout_count') || '0');
    if (timeoutCount >= 2) {
      console.warn('⚠️ [Login] Múltiples timeouts detectados, bloqueando login automático');
      console.warn('⚠️ [Login] Por favor, verifica tu conexión con Supabase antes de intentar iniciar sesión');
      console.warn('⚠️ [Login] Stack trace del intento bloqueado:');
      console.trace('⚠️ [Login] Llamada bloqueada desde:');
      return false;
    }
    
    // ✅ CRÍTICO: Marcar que hay un login en progreso para evitar que loadSession interfiera
    sessionStorage.setItem('login_in_progress', 'true');
    sessionStorage.setItem('login_start_time', Date.now().toString()); // ✅ NUEVO: Registrar tiempo de inicio
    console.log('🔐 [Login] Iniciando login para:', email);
    
    setLoading(true);
    
    try {
      
      // 1. signInWithPassword
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (signInError) {
        console.error('❌ [Login] Error en signInWithPassword:', signInError.message);
        sessionStorage.removeItem('login_in_progress');
        sessionStorage.removeItem('login_start_time');
        setLoading(false);
        return false;
      }
      
      if (!signInData.user) {
        console.error('❌ [Login] No se recibió usuario de signInWithPassword');
        sessionStorage.removeItem('login_in_progress');
        sessionStorage.removeItem('login_start_time');
        setLoading(false);
        return false;
      }
      
      console.log('✅ [Login] signInWithPassword exitoso');
      
      // ✅ OPTIMIZADO: Ejecutar getSession y getUser en paralelo
      console.log('🔄 [Login] Obteniendo sesión y usuario en paralelo...');
      const [sessionResult, userResult] = await Promise.all([
        supabase.auth.getSession(),
        supabase.auth.getUser()
      ]);
      
      const { data: { session }, error: sessionError } = sessionResult;
      const { data: { user }, error: userError } = userResult;
      
      if (sessionError || !session) {
        console.error('❌ [Login] Error al obtener sesión:', sessionError?.message);
        sessionStorage.removeItem('login_in_progress');
        sessionStorage.removeItem('login_start_time');
        setLoading(false);
        return false;
      }
      
      if (userError || !user) {
        console.error('❌ [Login] Error al obtener usuario:', userError?.message);
        sessionStorage.removeItem('login_in_progress');
        sessionStorage.removeItem('login_start_time');
        setLoading(false);
        return false;
      }
      
      // ✅ CORREGIDO: No refrescar manualmente - Supabase maneja el refresh automáticamente
      // Usar la sesión directamente después de login
      setSession(session);
      prevSessionTokenRef.current = session.access_token;
      console.log('✅ [Login] Sesión y usuario obtenidos en paralelo (refresh automático activo)');
      
      // 4. Consultar perfil según rol
      const userMetadata = user.user_metadata || {};
      const userRole = userMetadata.rol || userMetadata.role;
      const isAdmin = userRole === 'Administrador';
      const isContabilidad = userRole === 'Contabilidad';
      
      let profileData: any = null;
      
      // ✅ ELIMINADO: RPC - usar SELECT directo a profiles para todos los roles
      // El RPC no funciona, usar SELECT directo que ya funciona
      console.log('🔐 [Login] Obteniendo perfil desde tabla profiles');
      
      try {
        // ✅ CRÍTICO: Agregar timeout para evitar que se quede colgado por RLS
        // Usar el mismo patrón que en mockApi.ts (línea 432-436)
        const TIMEOUT_MS = 5000; // 5 segundos
        
        const profileQuery = supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        const profileTimeout = new Promise<{ data: null, error: { message: string; code?: string } }>((_, reject) => {
          setTimeout(() => reject(new Error('Test query timeout (5s)')), TIMEOUT_MS);
        });
        
        const result = await Promise.race([profileQuery, profileTimeout]) as any;
        const { data, error } = result;
        
        if (error) {
          if (error.code === 'PGRST116') {
            await supabase.auth.signOut();
            throw new Error('Tu cuenta está pendiente de aprobación. El administrador revisará tu solicitud y te notificará cuando sea aprobada.');
          }
          
          // ✅ Manejar timeout igual que en mockApi.ts (línea 463-470)
          if (error.message?.includes('timeout')) {
            console.error('❌ [Login] Timeout en consulta de profiles - posible problema de RLS o conectividad');
            console.error('💡 [Login] Verifica las políticas RLS de la tabla "profiles" en Supabase');
            profileData = null;
          }
          // ✅ Manejar errores RLS igual que en mockApi.ts (línea 447-450)
          else if (error.code === 'PGRST301' || 
                   error.message?.includes('permission') || 
                   error.message?.includes('policy') ||
                   error.message?.includes('RLS')) {
            console.error('⚠️ [Login] Error de permisos RLS detectado');
            console.error('💡 [Login] Verifica que exista una política RLS que permita:');
            console.error('   SELECT en profiles WHERE id = auth.uid()');
            profileData = null;
          } else {
            // Otros errores, también continuar sin profileData
            console.warn('⚠️ [Login] Error al obtener perfil, continuando con user_metadata:', error.message);
            profileData = null;
          }
        } else {
          profileData = data;
        }
      } catch (profileError: any) {
        // ✅ Manejar timeout en catch igual que en mockApi.ts (línea 463)
        if (profileError?.message?.includes('timeout')) {
          console.error('❌ [Login] Timeout al consultar profiles - posible problema de RLS o conectividad');
          console.error('💡 [Login] Verifica las políticas RLS de la tabla "profiles" en Supabase');
          profileData = null;
        } else {
          // Si falla la consulta, continuar sin profileData
          console.warn('⚠️ [Login] Error al consultar profiles, continuando con user_metadata:', profileError?.message);
          profileData = null;
        }
      }
      
      // ✅ CORREGIDO: Verificar acceso - SIEMPRE requerir profileData para verificar acceso
      if (!profileData) {
        console.error('❌ [Login] No se pudo obtener perfil del usuario');
        sessionStorage.removeItem('login_in_progress');
        sessionStorage.removeItem('login_start_time');
        await supabase.auth.signOut();
        throw new Error('No se pudo verificar tu cuenta. Por favor, intenta nuevamente o contacta al administrador.');
      }
      
      // 5. Verificar acceso - SIEMPRE verificar que acceso sea true
      if (profileData.acceso !== true && profileData.acceso !== 'true' && profileData.acceso !== 1) {
        console.warn('⚠️ [Login] Usuario sin acceso aprobado');
        sessionStorage.removeItem('login_in_progress');
        sessionStorage.removeItem('login_start_time');
        await supabase.auth.signOut();
        throw new Error('Tu cuenta está pendiente de aprobación. El administrador revisará tu solicitud y te notificará cuando sea aprobada.');
      }
      
      console.log('✅ [Login] Perfil y acceso verificados');
      
      // 6. Mapear y establecer usuario
      const mappedUser = await mapSupabaseUserToUser(user);
      if (!mappedUser) {
        console.error('❌ [Login] Error al mapear usuario');
        sessionStorage.removeItem('login_in_progress');
        sessionStorage.removeItem('login_start_time');
        setLoading(false);
        return false;
      }
      
      setUser(mappedUser);
      localStorage.setItem('currentUser', JSON.stringify(mappedUser));
      
      const elapsed = Date.now() - startTime;
      console.log(`✅ [Login] Login completado en ${elapsed}ms`);
      
      // ✅ CRÍTICO: Limpiar bandera de login en progreso después de éxito
      sessionStorage.removeItem('login_in_progress');
      sessionStorage.removeItem('login_start_time');
      
      // ✅ CRÍTICO: Establecer loading=false cuando el login es exitoso
      setLoading(false);
      console.log('✅ [Login] loading=false establecido después de login exitoso');
      
      return true;
      
    } catch (error: any) {
      console.error('❌ [Login] Error en login:', error);
      
      // ✅ CRÍTICO: Limpiar bandera de login en progreso en caso de error
      sessionStorage.removeItem('login_in_progress');
      sessionStorage.removeItem('login_start_time');
      
      // Si el error es sobre aprobación pendiente, propagarlo
      if (error.message && error.message.includes('pendiente')) {
        setLoading(false);
        throw error;
      }
      
      setLoading(false);
      return false;
    } finally {
      // ✅ Asegurar que la bandera se limpie siempre
      sessionStorage.removeItem('login_in_progress');
      sessionStorage.removeItem('login_start_time');
      setLoading(false);
    }
  };

  // Función de login (compatibilidad con código existente)
  const login = (userData: User) => {
    setUser(userData);
    localStorage.setItem('currentUser', JSON.stringify(userData));
  };

  // Función de registro
  const register = async (email: string, password: string, nombre: string): Promise<{ success: boolean; error?: string; warning?: string }> => {
    try {
      console.log('📝 [AuthContext] Iniciando register');
      setLoading(true);
      console.log('⏱️ [AuthContext] Loading establecido en true (register)');
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: undefined, // No redirigir después de confirmar email
          data: {
            nombre: nombre,
            rol: 'Usuario', // Por defecto, los nuevos usuarios son 'Usuario'
            empresa: 'J&J Operations',
            estado: 'pendiente' // Estado pendiente de aprobación
          }
        }
      });

      // IMPORTANTE: Verificar si el usuario se creó incluso si hay un error
      // Esto puede pasar cuando hay rate limits pero el usuario se crea exitosamente
      const userCreated = data?.user !== null && data?.user !== undefined;
      
      // Log para debugging
      console.log('🔍 Estado del registro:', {
        userCreated,
        hasError: !!error,
        errorMessage: error?.message,
        errorStatus: error?.status,
        userId: data?.user?.id
      });

      if (error) {
        console.error('Error en registro:', error);
        
        // ✅ NUEVO: Manejar error 500 del trigger (no es crítico si el usuario se creó)
        if (error.status === 500 && error.message?.includes('Database error saving new user')) {
          if (userCreated) {
            console.warn('⚠️ Error 500 del trigger, pero usuario creado. Creando perfil manualmente...');
            // Continuar con el flujo para crear el perfil manualmente
          } else {
            // Si el usuario NO se creó, retornar error
            return { 
              success: false, 
              error: 'Error del servidor al crear el usuario. Por favor, intenta nuevamente o contacta al administrador.' 
            };
          }
        }
        
        // Detectar si el error es solo del envío de email
        const isEmailError = error.message.includes('Error sending confirmation email') ||
                             error.message.includes('confirmation email') ||
                             (error.message.includes('email') && error.status === 500 && !error.message.includes('Database error'));
        
        // Si el usuario se creó pero hay un error de email (rate limit o 500), considerar éxito parcial
        if (userCreated && (error.message.includes('rate limit') || 
            error.message.includes('429') || 
            error.message.includes('email rate limit exceeded') ||
            error.status === 429 ||
            isEmailError)) {
          console.warn('⚠️ Usuario creado pero email no enviado:', isEmailError ? 'Error de servidor SMTP' : 'Rate limit');
          console.log('✅ Continuando con el registro - usuario creado exitosamente');
          // Continuar con el flujo normal - el usuario se creó exitosamente
        } else if (!userCreated && error.status !== 500) {
          // Solo retornar error si el usuario NO se creó
          // Error 429: Rate limit exceeded (límite de tasa de email excedido)
          if (error.message.includes('rate limit') || 
              error.message.includes('429') || 
              error.message.includes('email rate limit exceeded') ||
              error.status === 429) {
            return { 
              success: false, 
              error: 'Has realizado demasiados intentos de registro en poco tiempo. Supabase limita el envío de emails de confirmación para prevenir spam. Por favor, espera 15-30 minutos antes de intentar nuevamente, o contacta al administrador si necesitas crear una cuenta urgentemente.' 
            };
          }
          
          if (error.message.includes('already registered') || error.message.includes('already exists')) {
            return { 
              success: false, 
              error: 'Este correo electrónico ya está registrado. Por favor, intenta iniciar sesión o usa otro correo.' 
            };
          }
          
          if (error.message.includes('invalid email')) {
            return { 
              success: false, 
              error: 'El correo electrónico no es válido. Por favor, verifica que esté escrito correctamente.' 
            };
          }
          
          // Si es un error de envío de email pero el usuario no se creó
          // Esto puede pasar si SMTP no está configurado correctamente
          if (error.message.includes('Error sending confirmation email') || 
              (error.message.includes('email') && error.status === 500)) {
            return { 
              success: false, 
              error: 'No se pudo crear la cuenta debido a un problema con el servicio de email. El administrador debe configurar SMTP correctamente o deshabilitar la confirmación de email en Supabase Dashboard → Authentication → Emails → Templates → Desactivar "Enable email confirmations".' 
            };
          }
          
          return { success: false, error: error.message };
        }
      }

      // ✅ OPTIMIZADO: Solo crear perfil manualmente si el trigger falló
      // Si el usuario se creó sin errores, el trigger ya creó el perfil
      if (data?.user || userCreated) {
        const currentUser = data?.user;
        if (!currentUser) {
          console.error('❌ Usuario no disponible para crear perfil');
          return { success: false, error: 'Error al crear el perfil. El usuario fue creado pero no se pudo crear el perfil.' };
        }

        // ✅ NUEVO: Solo intentar crear perfil manualmente si hubo error 500 del trigger
        // Si no hubo error, el trigger ya creó el perfil y no necesitamos hacer nada
        const huboError500DelTrigger = error && error.status === 500 && error.message?.includes('Database error saving new user');
        
        if (huboError500DelTrigger && userCreated) {
          // Solo en este caso intentar crear el perfil manualmente
          console.log('⚠️ Trigger falló, creando perfil manualmente...');
          try {
            const profileData: any = {
              id: currentUser.id,
              full_name: nombre,
              email: email,
              rol: 'Usuario',
              acceso: false
            };
            
            const { error: profileError } = await supabase
              .from('profiles')
              .insert(profileData);

            if (profileError) {
              // Si es un error de duplicado, el perfil ya existe (del trigger o intento anterior)
              if (profileError.message.includes('duplicate') || 
                  profileError.message.includes('already exists') ||
                  profileError.code === '23505') {
                console.log('ℹ️ Perfil ya existe. Verificando acceso...');
                
                // Asegurar que el acceso sea false
                const { error: updateError } = await supabase
                  .from('profiles')
                  .update({ 
                    acceso: false,
                    rol: 'Usuario',
                    full_name: nombre,
                    email: email
                  })
                  .eq('id', currentUser.id);
                
                if (updateError) {
                  console.warn('⚠️ No se pudo actualizar acceso a false:', updateError);
                } else {
                  console.log('✅ Perfil actualizado: acceso=false');
                }
              } else {
                console.warn('⚠️ Error al crear perfil manualmente:', profileError);
              }
            } else {
              console.log('✅ Perfil creado manualmente con acceso=false');
            }
          } catch (profileErr) {
            console.warn('⚠️ Error al crear perfil manualmente (no crítico):', profileErr);
          }
        } else {
          // Si no hubo error 500, el trigger ya creó el perfil
          console.log('✅ Trigger creó el perfil automáticamente. No es necesario crearlo manualmente.');
        }

        // Si llegamos aquí, el usuario fue creado exitosamente
        const userEmail = currentUser.email || email;
        console.log('✅ Registro exitoso - Usuario creado:', userEmail);
        
        // ✅ NUEVO: Mensaje de éxito claro (sin warning, solo éxito)
        return { 
          success: true,
          warning: undefined // No mostrar warning, solo el mensaje de éxito
        };

      }

      console.error('❌ No se pudo crear el usuario - data.user es null');
      return { success: false, error: 'No se pudo crear el usuario' };
    } catch (error: any) {
      console.error('Error inesperado en registro:', error);
      return { success: false, error: error.message || 'Error al crear la cuenta' };
    } finally {
      console.log('✅ [AuthContext] Loading establecido en false (register finally)');
      setLoading(false);
    }
  };

  // Función para aprobar un usuario
  const approveUser = async (userId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // Obtener información del usuario antes de aprobar
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', userId)
        .single();

      // Actualizar el perfil para activarlo
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          acceso: true
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Error al aprobar usuario:', updateError);
        return { success: false, error: updateError.message };
      }

      // ✅ COMENTADO: No insertar en tabla auditoria, solo usar auditoría desde gastos
      // Registrar en auditoría
      // await supabase
      //   .from('auditoria')
      //   .insert({
      //     usuario: user?.nombre || 'Administrador',
      //     accion: 'APROBAR_USUARIO',
      //     detalle: `Usuario aprobado: ${profileData?.full_name || profileData?.email || userId}`,
      //     fecha: new Date().toISOString()
      //   });

      // Nota: La auditoría se genera automáticamente desde la tabla gastos.
      console.log(`✅ Usuario ${profileData?.email} aprobado exitosamente`);

      return { success: true };
    } catch (error: any) {
      console.error('Error al aprobar usuario:', error);
      return { success: false, error: error.message || 'Error al aprobar el usuario' };
    }
  };

  // Función para rechazar un usuario
  const rejectUser = async (userId: string, motivo?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // Actualizar el perfil para marcarlo como rechazado
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          acceso: false
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Error al rechazar usuario:', updateError);
        return { success: false, error: updateError.message };
      }

      // ✅ COMENTADO: No insertar en tabla auditoria, solo usar auditoría desde gastos
      // Registrar en auditoría
      // await supabase
      //   .from('auditoria')
      //   .insert({
      //     usuario: user?.nombre || 'Administrador',
      //     accion: 'RECHAZAR_USUARIO',
      //     detalle: `Usuario rechazado: ${userId}${motivo ? `. Motivo: ${motivo}` : ''}`,
      //     fecha: new Date().toISOString()
      //   });

      // Nota: La auditoría se genera automáticamente desde la tabla gastos.
      console.log(`✅ Usuario ${userId} rechazado`);
      return { success: true };
    } catch (error: any) {
      console.error('Error al rechazar usuario:', error);
      return { success: false, error: error.message || 'Error al rechazar el usuario' };
    }
  };

  // Función de logout
  const logout = async () => {
    try {
      console.log('🔄 [AuthContext] Iniciando proceso de logout...');
      console.log('🔄 [AuthContext] Usuario actual:', user?.correo, 'Rol:', user?.rol);
      
      // Marcar que estamos haciendo logout para evitar cargar datos locales después
      sessionStorage.setItem('logout_in_progress', 'true');
      sessionStorage.setItem('logout_time', Date.now().toString());
      
      // Establecer loading en false inmediatamente para evitar que se quede en "Verificando sesión"
      setLoading(false);
      
      // Limpiar estado local primero para evitar problemas
      setUser(null);
      setSession(null);
      localStorage.removeItem('currentUser');
      
      // ✅ CRÍTICO: Limpiar todos los tokens de Supabase antes de cerrar sesión
      clearSupabaseTokens();
      
      // Intentar cerrar sesión en Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.warn('⚠️ [AuthContext] Error al cerrar sesión en Supabase:', error);
        // Continuar de todas formas, ya limpiamos el estado local
      } else {
        console.log('✅ [AuthContext] Sesión cerrada exitosamente en Supabase');
      }
      
      // Limpiar la bandera después de un delay para que loadSession la vea si se ejecuta
      // El delay debe ser suficiente para que loadSession detecte la bandera
      setTimeout(() => {
        sessionStorage.removeItem('logout_in_progress');
        sessionStorage.removeItem('logout_time');
      }, 2000);
      
      // Asegurar que loading siga siendo false
      setLoading(false);
      
    } catch (error) {
      console.error('❌ [AuthContext] Error inesperado al cerrar sesión:', error);
      // Fallback: limpiar estado local aunque falle Supabase
      setUser(null);
      setSession(null);
      localStorage.removeItem('currentUser');
      // ✅ CRÍTICO: Limpiar tokens también en caso de error
      clearSupabaseTokens();
      sessionStorage.removeItem('logout_in_progress');
      sessionStorage.removeItem('logout_time');
      // Asegurar que loading sea false
      setLoading(false);
    }
  };

  // Función para cambiar rol (solo para demostración)
  const switchRole = (role: UserRole) => {
    if (user) {
      const updatedUser = { ...user, rol: role };
      setUser(updatedUser);
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    }
  };

  // Función para verificar permisos
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    return ROLE_PERMISSIONS[user.rol]?.includes(permission) || false;
  };

  // Getters para roles específicos
  const isAdmin = user?.rol === 'Administrador';
  const isContadora = user?.rol === 'Contadora';
  const isUsuario = user?.rol === 'Usuario';

  const value: AuthContextType = {
    user,
    loading,
    session,
    login,
    loginWithEmail,
    register,
    approveUser,
    rejectUser,
    logout,
    switchRole,
    hasPermission,
    isAdmin,
    isContadora,
    isUsuario
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Función para obtener todos los usuarios (para el selector)
export const getMockUsers = () => MOCK_USERS;
